import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import i18nInstance from 'i18next'
import { useAuthStore } from '@/stores/auth'

/**
 * Applies the signed-in account's language choice, and keeps `<html lang>`
 * honest. It no longer PERSISTS anything — `lib/language.ts` owns that.
 *
 * The persistence used to live here as `i18n.on('languageChanged', …)`, which
 * wrote every language change back to the profile. That was safe only while a
 * change could come from nowhere but the switcher. An event now declares its
 * own language (N-13), so the listener would have written "this event is in
 * Norwegian" into the account as "this person prefers Norwegian" — a page-level
 * default silently promoted to a permanent personal setting, for anyone who
 * simply opened a link. One writer, and it is the one a human presses.
 *
 * `language_preference` is nullable since 20260807_language_precedence: NULL is
 * an account that has never chosen, and the guard below is what lets it fall
 * through to the event, then the browser, then nb.
 *
 * The WRITE goes through the module singleton, not through `useTranslation()`'s
 * `i18n`. That object is not referentially stable — it changes identity on every
 * `languageChanged` — so `[preference, i18n]` armed the effect to re-trigger
 * itself off its own write. Dormant here only because `preference` almost always
 * already matches the current language, and i18next emits nothing for a no-op
 * change; it is the same loop that killed `useEventLanguage` with 55 runs and
 * «Maximum update depth exceeded». `check-i18n-deps` now fails the build on the
 * pattern rather than trusting anyone to remember. `t`/`i18n.language` are still
 * read from the hook, because those are the values a render must subscribe to.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const preference = useAuthStore((s) => s.profile?.language_preference)

  useEffect(() => {
    if (preference) void i18nInstance.changeLanguage(preference)
  }, [preference])

  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  return <>{children}</>
}
